
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function InsurancePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const insuranceBenefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Lower Premiums',
      description: 'Documented security measures and risk mitigation strategies can qualify you for significant insurance premium reductions.',
      stat: 'Up to 25%',
      statLabel: 'Premium Savings'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Stronger Claims',
      description: 'Detailed documentation and professional assessments provide the evidence needed to support and expedite insurance claims.',
      stat: '3x Faster',
      statLabel: 'Claim Processing'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Reduced Liability',
      description: 'Proactive risk management demonstrates due diligence, protecting you from liability disputes and coverage denials.',
      stat: '90%',
      statLabel: 'Dispute Prevention'
    },
    {
      icon: 'ri-file-damage-line',
      title: 'Pre-Loss Documentation',
      description: 'Comprehensive property records before any incident ensure accurate valuations and smoother claims processes.',
      stat: '100%',
      statLabel: 'Documentation Coverage'
    }
  ];

  const documentationServices = [
    {
      title: 'Property Condition Reports',
      description: 'Detailed photographic and written documentation of your property\'s current state, including all systems, structures, and valuable assets.',
      icon: 'ri-camera-line'
    },
    {
      title: 'Security System Certification',
      description: 'Professional verification of installed security measures that insurance companies recognize for premium discounts.',
      icon: 'ri-lock-line'
    },
    {
      title: 'Maintenance Records',
      description: 'Organized documentation of all preventative maintenance, repairs, and upgrades demonstrating responsible property stewardship.',
      icon: 'ri-tools-line'
    },
    {
      title: 'Risk Assessment Reports',
      description: 'Comprehensive vulnerability analysis with documented mitigation measures that satisfy insurance requirements.',
      icon: 'ri-search-eye-line'
    },
    {
      title: 'Storm Preparedness Plans',
      description: 'Documented emergency procedures and protective measures that can reduce weather-related claim denials.',
      icon: 'ri-thunderstorms-line'
    },
    {
      title: 'Incident Response Protocols',
      description: 'Established procedures for handling emergencies that demonstrate proactive risk management to insurers.',
      icon: 'ri-alarm-warning-line'
    }
  ];

  const claimSupportFeatures = [
    {
      phase: 'Before Incident',
      items: [
        'Complete property inventory with photos and valuations',
        'Baseline condition documentation',
        'Security system verification records',
        'Maintenance history compilation',
        'Risk mitigation evidence'
      ]
    },
    {
      phase: 'During Incident',
      items: [
        'Real-time damage documentation',
        'Emergency response coordination',
        'Immediate protective measures',
        'Communication with authorities',
        'Evidence preservation'
      ]
    },
    {
      phase: 'After Incident',
      items: [
        'Comprehensive damage assessment',
        'Before/after comparison reports',
        'Contractor coordination for estimates',
        'Claims documentation support',
        'Adjuster meeting preparation'
      ]
    }
  ];

  const insurancePartnerBenefits = [
    {
      title: 'For Property Owners',
      benefits: [
        'Reduced annual premiums through documented security measures',
        'Faster claim approvals with professional documentation',
        'Protection against claim denials due to negligence allegations',
        'Accurate property valuations for adequate coverage',
        'Peace of mind knowing you\'re properly protected'
      ],
      icon: 'ri-home-heart-line',
      color: 'teal'
    },
    {
      title: 'For HOAs & Property Managers',
      benefits: [
        'Demonstrate fiduciary responsibility to residents',
        'Reduce association liability exposure',
        'Maintain compliance with insurance requirements',
        'Streamline claims for common areas and shared systems',
        'Protect reserve funds from unexpected losses'
      ],
      icon: 'ri-building-2-line',
      color: 'amber'
    },
    {
      title: 'For Commercial Properties',
      benefits: [
        'Business interruption documentation',
        'Asset protection verification',
        'Liability risk mitigation records',
        'Regulatory compliance documentation',
        'Multi-location consistency'
      ],
      icon: 'ri-store-2-line',
      color: 'slate'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/169cce1da0808bf5e888749d7cd13216.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-2 mb-6">
              <i className="ri-shield-star-line text-teal-400"></i>
              <span className="text-teal-300 text-sm font-medium">Insurance Optimization</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Maximize Your Insurance Benefits with SafePoint
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Our law enforcement expertise and comprehensive documentation services help you secure better coverage, lower premiums, and faster claims resolution.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="/#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Get Insurance Assessment</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="tel:786-447-6283"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span>786-447-6283</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The SafePoint Insurance Advantage
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional documentation and risk management that insurance companies recognize and reward
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {insuranceBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 text-center group"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors">
                  <i className={`${benefit.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <div className="text-4xl font-bold text-teal-600 mb-2">{benefit.stat}</div>
                <div className="text-sm text-gray-500 mb-4">{benefit.statLabel}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Documentation Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Professional Documentation Matters
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Insurance companies make decisions based on evidence. Without proper documentation, you're at a significant disadvantage when filing claims or negotiating premiums.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-2xl text-red-500"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Without Documentation</h4>
                    <p className="text-gray-600 text-sm">Claims denied due to lack of evidence, disputes over property condition, higher premiums from perceived risk</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">With SafePoint Documentation</h4>
                    <p className="text-gray-600 text-sm">Expedited claims with clear evidence, accurate valuations, premium discounts for demonstrated risk management</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 p-6 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-4">
                  <i className="ri-lightbulb-line text-2xl text-amber-600 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Did You Know?</h4>
                    <p className="text-gray-700 text-sm">
                      According to industry data, <strong>over 40% of insurance claims</strong> are underpaid or denied due to insufficient documentation. Professional pre-loss documentation can increase claim payouts by an average of <strong>30-50%</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div 
                className="w-full h-[500px] rounded-2xl bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20property%20inspector%20with%20tablet%20documenting%20modern%20home%20exterior%20taking%20detailed%20photos%20and%20notes%20clipboard%20in%20hand%20wearing%20professional%20attire%20sunny%20day%20well%20maintained%20residential%20property%20in%20background%20clean%20professional%20photography&width=600&height=500&seq=insurance-doc-1&orientation=portrait)'
                }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl text-teal-600"></i>
                  </div>
                  <span className="font-bold text-slate-900">Comprehensive Reports</span>
                </div>
                <p className="text-sm text-gray-600">Every SafePoint assessment includes detailed documentation accepted by major insurance carriers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Services */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Insurance-Ready Documentation Services
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Professional documentation that meets and exceeds insurance industry standards
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {documentationServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-teal-500/50 hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-teal-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-500/30 transition-colors">
                  <i className={`${service.icon} text-2xl text-teal-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Claims Support Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Complete Claims Support Lifecycle
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              SafePoint supports you through every phase of the insurance process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {claimSupportFeatures.map((phase, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 h-full">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
                    index === 0 ? 'bg-blue-100 text-blue-700' :
                    index === 1 ? 'bg-amber-100 text-amber-700' :
                    'bg-teal-100 text-teal-700'
                  }`}>
                    <i className={`${
                      index === 0 ? 'ri-calendar-check-line' :
                      index === 1 ? 'ri-alarm-warning-line' :
                      'ri-file-check-line'
                    } text-lg`}></i>
                    <span className="font-semibold text-sm">{phase.phase}</span>
                  </div>
                  
                  <ul className="space-y-4">
                    {phase.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-lg mt-0.5"></i>
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-gray-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits by Property Type */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Insurance Benefits by Property Type
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored insurance optimization strategies for every property category
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {insurancePartnerBenefits.map((category, index) => (
              <div 
                key={index}
                className={`rounded-xl p-8 ${
                  category.color === 'teal' ? 'bg-teal-600' :
                  category.color === 'amber' ? 'bg-amber-600' :
                  'bg-slate-800'
                } text-white`}
              >
                <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${category.icon} text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-6">{category.title}</h3>
                <ul className="space-y-4">
                  {category.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-lg mt-0.5 opacity-80"></i>
                      <span className="text-sm opacity-90">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Law Enforcement Advantage */}
      <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div 
              className="w-full h-[450px] rounded-2xl bg-cover bg-center shadow-2xl order-2 lg:order-1"
              style={{
                backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20security%20expert%20reviewing%20property%20documentation%20on%20tablet%20with%20security%20camera%20footage%20visible%20modern%20office%20setting%20with%20monitors%20showing%20property%20surveillance%20clean%20professional%20corporate%20environment%20teal%20accent%20lighting&width=600&height=450&seq=insurance-le-1&orientation=landscape)'
              }}
            />
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-2 mb-6">
                <i className="ri-shield-user-line text-teal-400"></i>
                <span className="text-teal-300 text-sm font-medium">Law Enforcement Expertise</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                The Law Enforcement Advantage in Insurance
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Our 20+ years of state and federal law enforcement experience provides unique advantages when working with insurance companies.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-search-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Evidence-Grade Documentation</h4>
                    <p className="text-gray-400 text-sm">Our reports meet the same evidentiary standards used in legal proceedings</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Trained Observation Skills</h4>
                    <p className="text-gray-400 text-sm">We identify and document details that others miss, strengthening your claims</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-voice-line text-2xl text-teal-400"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">Professional Credibility</h4>
                    <p className="text-gray-400 text-sm">Insurance adjusters recognize and respect law enforcement documentation standards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Optimize Your Insurance Coverage?
          </h2>
          <p className="text-xl text-teal-100 mb-10 leading-relaxed">
            Contact SafePoint today for a free consultation and learn how our professional documentation services can save you money and protect your property.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Schedule Free Consultation</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="tel:786-447-6283"
              className="inline-flex items-center gap-2 px-8 py-4 bg-teal-700 text-white font-semibold rounded-lg hover:bg-teal-800 transition-colors border border-teal-500 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              <span>Call 786-447-6283</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
